import { Button, PageError, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { paymentTermsQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import { type PaymentTerm, TERM_KIND, type TermKind, termActions } from "../../invoices";
import styles from "./invoices.module.css";

const KINDS = Object.keys(TERM_KIND) as TermKind[];
const hasDays = (kind: TermKind) => kind === "net_days" || kind === "end_of_month";

/** "Net 30 days", "End of month + 10 days", "Paid on delivery". */
export function termRule(t: Pick<PaymentTerm, "kind" | "days" | "grace_days">): string {
  const base =
    t.kind === "net_days"
      ? `${t.days} days after the invoice`
      : t.kind === "end_of_month"
        ? `${t.days} days after the month ends`
        : TERM_KIND[t.kind];
  return t.grace_days ? `${base}, ${t.grace_days} days' grace` : base;
}

/**
 * Payment terms: how a customer or business pays, and when an invoice to them is due. Code and
 * kind never change (orders and invoices point at the term): add a new term instead.
 */
export function TermsView(): ReactNode {
  const terms = useQuery(paymentTermsQuery);
  const [editing, setEditing] = useState<PaymentTerm | "new" | null>(null);
  if (terms.status === "pending") return <PageLoading label="Loading payment terms" />;
  if (terms.status === "error")
    return <PageError message={messageFor(errorOf(terms.error))} onRetry={() => terms.refetch()} />;
  return (
    <div className={styles.panel}>
      <div className={styles.actions}>
        <Button variant="secondary" size="sm" onClick={() => setEditing("new")}>
          Add a term
        </Button>
      </div>
      <SortableDataTable
        caption="Payment terms"
        rowKey="id"
        rows={terms.data}
        itemName={{ one: "term", other: "terms" }}
        columns={[
          {
            key: "name",
            label: "Term",
            sortable: false,
            render: (_v, t) => (
              <span>
                {t.name} {t.is_default ? <Badge tone="info">Default</Badge> : null}
              </span>
            ),
          },
          { key: "kind", label: "Due", sortable: false, render: (_v, t) => termRule(t) },
          { key: "code", label: "Code", sortable: false },
          {
            key: "is_active",
            label: "Status",
            sortable: false,
            render: (_v, t) => (
              <Badge tone={t.is_active ? "success" : "neutral"}>{t.is_active ? "In use" : "Retired"}</Badge>
            ),
          },
          {
            key: "actions",
            label: "",
            sortable: false,
            render: (_v, t) => (
              <Button variant="ghost" size="sm" onClick={() => setEditing(t)}>
                Edit
              </Button>
            ),
          },
        ]}
      />
      <p className={styles.muted}>
        Prepaid orders aren't dispatched until paid. Credit terms (net days, end of month) need a credit limit on the
        customer or business.
      </p>
      {editing ? <TermDialog term={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function TermDialog({ term, onClose }: { term?: PaymentTerm; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(term?.name ?? "");
  const [code, setCode] = useState(term?.code ?? "");
  const [kind, setKind] = useState<TermKind>(term?.kind ?? "net_days");
  const [days, setDays] = useState(term ? String(term.days) : "30");
  const [grace, setGrace] = useState(term ? String(term.grace_days) : "0");
  const [isDefault, setIsDefault] = useState(term?.is_default ?? false);
  const [active, setActive] = useState(term?.is_active ?? true);
  // the API's range for days and grace_days: 0 to 365
  const whole = (v: string) => /^(\d{1,2}|[0-2]\d{2}|3[0-5]\d|36[0-5])$/.test(v);
  const check = useFormCheck();
  const errors = {
    name: !name.trim() ? "Enter the term's name." : name.trim().length > 80 ? "At most 80 characters." : undefined,
    code: term || /^[a-z0-9_]{1,40}$/.test(code) ? undefined : "Up to 40 lowercase letters, digits or underscores.",
    days: !hasDays(kind) || whole(days) ? undefined : "A whole number of days, 0 to 365.",
    grace: whole(grace) ? undefined : "A whole number of days, 0 to 365.",
  };
  const mutation = useMutation({
    mutationFn: () => {
      // number-ok: a count of days, not money
      const n = (v: string) => Number.parseInt(v, 10);
      const timing = { days: hasDays(kind) ? n(days) : undefined, grace_days: n(grace) };
      return term
        ? termActions.update(term.id, {
            name: name.trim(),
            ...timing,
            is_active: active,
            ...(isDefault && !term.is_default ? { is_default: true as const } : {}),
          })
        : termActions.create({ code, name: name.trim(), kind, ...timing, is_default: isDefault });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payment-terms"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={term ? `Edit ${term.name}` : "Add a payment term"}
        description={term ? "Code and kind stay as they are: add a new term to change them." : undefined}
      >
        <div className={styles.form}>
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Net 14 days"
            error={check.show(errors.name)}
          />
          {term ? null : (
            <div className={styles.pair}>
              <Input
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="net_14"
                description="Lowercase letters, digits and _. Can't change later."
                error={check.show(errors.code)}
              />
              <Select
                label="Kind"
                value={kind}
                onValueChange={(v) => setKind(v as TermKind)}
                options={KINDS.map((k) => ({ value: k, label: TERM_KIND[k] }))}
              />
            </div>
          )}
          <div className={styles.pair}>
            {hasDays(kind) ? (
              <Input
                label="Days"
                inputMode="numeric"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                error={check.show(errors.days)}
              />
            ) : null}
            <Input
              label="Grace days"
              inputMode="numeric"
              value={grace}
              onChange={(e) => setGrace(e.target.value)}
              description="Extra days before a payer counts as late. The due date doesn't move."
              error={check.show(errors.grace)}
            />
          </div>
          {term?.is_default ? null : (
            <Switch label="The company's default term" checked={isDefault} onCheckedChange={setIsDefault} />
          )}
          {term && !term.is_default ? <Switch label="In use" checked={active} onCheckedChange={setActive} /> : null}
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={mutation.isPending} onClick={() => check.submit(errors, () => mutation.mutate())}>
              {term ? "Save term" : "Add term"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
